import type { ReactNode } from 'react'
import Markdown from 'react-markdown'
import { twMerge } from 'tailwind-merge'
import { ChevronDown, ChevronUp, Sparkles, XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'

type AiSummaryCardProps = {
  /* Heading shown beside the icon, also used as the region's accessible name. */
  title: string
  /* Decorative icon before the title. Defaults to sparkles. */
  icon?: ReactNode
  /* AI-generated Markdown, clamped to a short preview until expanded. */
  summary: string
  expanded: boolean
  onToggle: () => void
  onDismiss: () => void
  className?: string
}

/* The card's controls use brand-700, darker than the plain button default, so they sit closer to the card's text. */
const controlColor = '!text-brand-700'

/** Dismissible panel for an AI-generated summary, previewed in a few lines with a Show More toggle. */
export function AiSummaryCard({
  title,
  icon = <Sparkles className="[&_path]:fill-current" />,
  summary,
  expanded,
  onToggle,
  onDismiss,
  className,
}: AiSummaryCardProps) {
  return (
    <section
      data-ds-component="AiSummaryCard"
      aria-label={title}
      className={twMerge('relative flex flex-col gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 pb-2 pt-4 text-brand-800', className)}
    >
      <div className="flex flex-col gap-1.5">
        {/* Right padding keeps a long title clear of the close button. */}
        <div className="flex min-h-6 items-center gap-1 pr-6 font-rethink-sans text-base font-semibold">
          <span aria-hidden="true" className="flex shrink-0 [&_svg]:size-4">
            {icon}
          </span>
          {title}
        </div>
        <Markdown
          components={{
            h2: ({ children }) => (
              <p>
                <strong>{children}</strong>
              </p>
            ),
          }}
          className={`text-xs leading-4 [&>*]:my-4 [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 ${
            expanded ? '' : 'line-clamp-3'
          }`}
        >
          {summary}
        </Markdown>
      </div>
      <IconButton
        variant="plain"
        size="sm"
        icon={<XClose />}
        aria-label={`Close ${title}`}
        onClick={onDismiss}
        className={controlColor}
        wrapperClassName="absolute right-1 top-1.5"
      />
      <Button
        variant="plain"
        size="sm"
        leadingIcon={expanded ? <ChevronUp /> : <ChevronDown />}
        aria-expanded={expanded}
        onClick={onToggle}
        className={`self-center ${controlColor}`}
      >
        {expanded ? 'Show Less' : 'Show More'}
      </Button>
    </section>
  )
}
